import { useState } from 'react';
import { httpClient } from '@/httpClient/axios.ts';
import { useForm } from '@/form/useForm';
import { useAppConfig } from '@/appConfig/useAppConfig';

interface UsePeselCheckApi {
  peselStatus: PeselStatus;
  peselCheck: () => Promise<void>;
}

export type PeselStatus =
  | 'unknown'
  | 'checking'
  | 'not-registered'
  | 'poltransplant-offline'
  | 'already-registered-dkms'
  | 'already-registered-other'
  | 'invalid';
export type PeselStatusApiResult =
  | 'UNDEFINED'
  | 'NOT_REGISTERED'
  | 'REGISTERED_DKMS'
  | 'REGISTERED_OTHER'
  | 'INVALID'
  | 'ERROR';

export const PESEL_SKIP_CODES = ['99999999999'] as const;

export const isPeselSkipCode = (pesel: string | null | undefined): boolean => {
  if (!pesel) {
    return false;
  }

  return PESEL_SKIP_CODES.includes(pesel as (typeof PESEL_SKIP_CODES)[number]);
};

const mapApiToUiStatus = (apiStatus: PeselStatusApiResult): PeselStatus => {
  switch (apiStatus) {
    case 'NOT_REGISTERED':
      return 'not-registered';
    case 'REGISTERED_DKMS':
      return 'already-registered-dkms';
    case 'REGISTERED_OTHER':
      return 'already-registered-other';
    case 'INVALID':
      return 'invalid';
    case 'ERROR':
    case 'UNDEFINED':
      return 'poltransplant-offline';
    default:
      return 'poltransplant-offline'; // fallback
  }
};

export const usePeselCheck = ({ spinnerDuration = 10000 }): UsePeselCheckApi => {
  const [peselStatus, setPeselStatus] = useState<PeselStatus>('unknown');
  const { getFieldValue } = useForm();
  const meta = useAppConfig();

  const peselCheck = () => {
    if (!meta) {
      return Promise.reject();
    }

    setPeselStatus('checking');
    const pesel = getFieldValue('pesel', 'raw');
    const url = `${meta.api.peselCheck}/${pesel}`;

    if (isPeselSkipCode(pesel as string | null | undefined)) {
      setPeselStatus('not-registered');
      return Promise.resolve();
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      setPeselStatus('poltransplant-offline');
    }, 5000); // 5 Sekunden Timeout

    return httpClient
      .get(url, { signal: controller.signal })
      .then((response) => {
        clearTimeout(timeoutId); // Timeout abbrechen, wenn die Antwort rechtzeitig kam
        setTimeout(() => {
          setPeselStatus(mapApiToUiStatus(response?.data?.check_result));
        }, spinnerDuration);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setTimeout(() => {
            setPeselStatus('poltransplant-offline');
          }, spinnerDuration);
        }
        // Im Falle von AbortError wird der Status bereits im Timeout gesetzt
      });
  };

  return { peselStatus, peselCheck };
};
